import * as THREE from "three";

/**
 * The twelve proportion sliders, as bone transforms on a loaded cat.
 *
 * Nothing here rebuilds geometry: a morph is a handful of bone scales written
 * once per apply(), plus translations and rotation offsets re-applied every
 * frame in postMixer(), because the clips own bone.position and
 * bone.quaternion outright and the next mixer.update() reverts anything
 * written to them once.
 *
 * Every bone scale here is UNIFORM, on purpose.  A non-uniform scale above an
 * animated joint shears the subtree as the joint rotates, and its effect on a
 * child translation depends on which way the child's rest frame points --
 * per-axis bookkeeping that the toon armature's -90 deg X rotation gets wrong
 * the moment "Y is down" is assumed.  A uniform scale commutes with any
 * rotation, and what it does to child translations is one scalar, which
 * postMixer() cancels with a scalar multiplier wherever the inheritance is
 * unwanted.  Girth/length independence falls out of the split: leg_thickness
 * is a uniform scale on the leg root, leg_length is the translation factor
 * length/thickness on every segment below it.  The lone exception is the tail
 * tip, a leaf with nothing below it to distort, which stretches along its own
 * rest direction so the last fifth of the tail lengthens with the rest.
 *
 *   size           scene root       uniform scale.  The origin sits at the
 *                                   paws and main.js/cat.js write only
 *                                   root.position and rotation.y, never
 *                                   scale, so the cat grows about the ground
 *                                   it stands on and the paws stay planted.
 *   chonk          hips|torso       uniform scale c, countered at the neck,
 *                                   leg roots and tail root (scale and
 *                                   translation x 1/c) so only the torso
 *                                   fattens and nothing changes height.
 *   body_length    spine links      translation x length/c.
 *   leg_length     leg segments     translation x length/thickness, plus a
 *                                   torso lift -- see LIFT below.
 *   leg_thickness  leg roots        uniform scale x thickness/c.
 *   neck_length    head             translation x length; the head-from-neck
 *                                   offset IS the neck segment.
 *   head_size      head             uniform scale.  Ears counter it.
 *   muzzle         jaw|mouth        uniform scale, plus a translation push
 *                                   along the head's rest forward only --
 *                                   scaling the whole jaw offset would drop
 *                                   the jaw and read as an open mouth.
 *   ear_size       ears             uniform scale x ear/head, so the two
 *                                   sliders stay independent while the ear
 *                                   bases still ride the scaled skull.
 *   ear_tilt       ears             rotation offset about the cat's rest
 *                                   forward axis, mirrored so positive splays
 *                                   the pair outward.
 *   tail_length    tail links       translation x length/fluff.
 *   tail_fluff     tail root        uniform scale x fluff/c.
 *   tail_curl      every tail bone  rotation offset about the cat's rest
 *                                   left axis; per-link, so 30 deg is a full
 *                                   curl over the back across five joints.
 *
 * LIFT: longer legs would push the paws through the floor, so the torso bone
 * rides up by (leg_length - 1) x the mean rest drop of the four legs,
 * root-to-lowest-descendant.  Front and hind drops differ (~0.21 vs ~0.18 m
 * on the generated rigs) and one root can only rise one amount, so the mean
 * leaves paws within ~6 mm of the ground at the slider's extremes -- inside
 * what the animated leg bend hides.  The lift is added to the torso bone's
 * position along a captured local-up of its parent frame, so the toon
 * armature's rotation and unit scale are handled by the same arithmetic and
 * the lift rides inside the root's `size` scale like the legs do.
 *
 * REST BASIS.  The customizer builds a CatMorph the first time a look is
 * applied -- mid-animation -- so bone.position holds an animation frame, not
 * the rest pose.  Local state is captured as found anyway, and that is exact
 * in effect: the mixer never writes bone.scale (the clips carry only rotation
 * and translation tracks), and any channel it does write gets re-posed on the
 * next update, so nothing this class computes may depend on a captured local
 * position or rotation being "rest".  What DOES need the rest pose -- the leg
 * drops and the ear/tail/jaw axes -- comes from the skeleton's inverse bind
 * matrices instead, which encode the bind pose no matter what the mixer has
 * done.  They are used only as a coherent world snapshot, never per-link: the
 * toon's bind chain carries FBX unit-conversion pairs (a 0.01 on root_01,
 * x100 into torso_02) that make per-link bind locals garbage while the world
 * products stay consistent.  That world snapshot is scene-aligned but not
 * metric for the toon (uniformly ~8.2x true size), so it is calibrated by
 * comparing bone segment lengths against the live skeleton's -- lengths are
 * rigid, so the current pose does not matter.
 *
 * postMixer() runs at 60 Hz over ~35 bones and allocates nothing: every
 * vector and quaternion it touches is hoisted into the records built here.
 * Bones the mixer animates get position/quaternion rewritten before it runs;
 * bones it does not would compound a factor applied to their current value,
 * so each record keeps the last value it wrote and re-bases only when the
 * bone was written by someone else since.  apply() then never compounds
 * either: it just changes the factors the next postMixer() applies.
 */

const RAD = THREE.MathUtils.degToRad;

/** The cats face +Z in their own scene, +X is the cat's own left. */
const FORWARD = new THREE.Vector3(0, 0, 1);
const LEFT = new THREE.Vector3(1, 0, 0);

/**
 * A name, family-blind: lowercased, one trailing _NN index stripped,
 * separators dropped.  "spine_01" and "spine.01_012" both land on a spine
 * link, "ear_L" and "ear.L_019" (or "earL_019" after the loader sanitises
 * the dots away) both land on "earl".
 */
function normName(name) {
  return name.toLowerCase().replace(/_\d+$/, "").replace(/[._\s-]+/g, "");
}

/** Control bones -- IK targets, the toon's tail.CTRL -- carry no skin. */
const CONTROL = /ik|ctrl/;

export class CatMorph {
  constructor(root) {
    this.root = root;
    this._scale0 = root.scale.clone();
    root.updateMatrixWorld(true);
    const rootInv = new THREE.Matrix4().copy(root.matrixWorld).invert();

    // ---- the skeleton, and its bind pose as a world snapshot -------------
    const bindWorld = new Map();               // Bone -> Matrix4, bind space
    root.traverse((o) => {
      if (!o.isSkinnedMesh) return;
      const bones = o.skeleton.bones, inv = o.skeleton.boneInverses;
      for (let i = 0; i < bones.length; i++) {
        if (!bindWorld.has(bones[i])) {
          bindWorld.set(bones[i], new THREE.Matrix4().copy(inv[i]).invert());
        }
      }
    });

    this._rest = [];                           // dispose() restores these
    const cur = new Map();                     // Bone -> its rest record
    const rest = new Map();                    // Bone -> { y, wq } bind pose
    const wp = new THREE.Vector3(), ws = new THREE.Vector3();
    for (const [bone, world] of bindWorld) {
      const r = { bone, p: bone.position.clone(), q: bone.quaternion.clone(),
                  s: bone.scale.clone() };
      this._rest.push(r);
      cur.set(bone, r);
      const wq = new THREE.Quaternion();
      world.decompose(wp, wq, ws);
      rest.set(bone, { y: world.elements[13], wq });
    }

    // Bind-space units per world metre, read off segment lengths: a bone's
    // distance to its parent is rigid, so the live skeleton gives the metric
    // length whatever pose the mixer left it in.  The median shrugs off the
    // toon's two unit-conversion links.
    const ratios = [];
    const bp = new THREE.Vector3(), pp = new THREE.Vector3();
    for (const [bone, world] of bindWorld) {
      const parentWorld = bindWorld.get(bone.parent);
      if (!parentWorld) continue;
      bp.setFromMatrixPosition(world);
      pp.setFromMatrixPosition(parentWorld);
      const bindLen = bp.distanceTo(pp);
      bp.setFromMatrixPosition(bone.matrixWorld);
      pp.setFromMatrixPosition(bone.parent.matrixWorld);
      const liveLen = bp.distanceTo(pp);
      if (bindLen > 1e-5 && liveLen > 1e-5) ratios.push(liveLen / bindLen);
    }
    ratios.sort((a, b) => a - b);
    const metre = ratios.length ? ratios[ratios.length >> 1] : 1;

    // ---- roles -----------------------------------------------------------
    let torso = null, neck = null, head = null, jaw = null;
    const spine = [], ears = [], tails = [], legRoots = [], legSegs = [];
    for (const bone of bindWorld.keys()) {
      const n = normName(bone.name);
      if (CONTROL.test(n)) continue;
      if (n === "hips" || n === "torso") torso = bone;
      else if (/^spine\d*$/.test(n) || n === "chest") spine.push(bone);
      else if (n === "neck") neck = bone;
      else if (n === "head") head = bone;
      else if (n === "jaw" || n === "mouth") jaw = bone;
      else if (n === "earl" || n === "earr") ears.push([bone, n === "earl" ? -1 : 1]);
      else if (/^tail(\d+|end)?$/.test(n)) tails.push(bone);
      else if (/^(clavicle|thigh|legupperf)/.test(n)) legRoots.push(bone);
      else if (/^(upperarm|forearm|frontcannon|frontpaw|shin|hindcannon|hindpaw|legupperb|leglower|foot)/.test(n)) legSegs.push(bone);
      // Anything else -- eyes, root bones -- is left alone and inherits.
    }

    // The generated tails all normalise to "tail", so the chain is resolved
    // by structure: the root is the one whose parent is not a tail bone, the
    // tip the one with no tail child.
    const tailSet = new Set(tails);
    let tailRoot = null, tailTip = null;
    const tailLinks = [];
    for (const b of tails) {
      if (tailSet.has(b.parent)) tailLinks.push(b);
      else tailRoot = b;
      if (!b.children.some((c) => tailSet.has(c))) tailTip = b;
    }

    // ---- rest measurements ----------------------------------------------
    // Mean vertical drop of the scaled leg segments, in metres: root to the
    // lowest descendant in the bind pose.  Exactly the part leg_length
    // multiplies, whatever the segments' zigzag.
    let dropSum = 0, dropN = 0;
    for (const r of legRoots) {
      let lo = Infinity;
      const walk = (b) => {
        const i = rest.get(b);
        if (i) lo = Math.min(lo, i.y);
        for (const c of b.children) if (c.isBone) walk(c);
      };
      walk(r);
      const top = rest.get(r).y;
      if (lo < top) { dropSum += (top - lo) * metre; dropN++; }
    }
    this._drop = dropN ? dropSum / dropN : 0;

    // World-up in the torso bone's parent frame, unit scale included, so a
    // lift in metres lands as metres whatever the armature's orientation.
    // The parent chain above the torso bone is root joints and armature
    // nodes, which none of the clips meaningfully rotate.
    this._up = new THREE.Vector3(0, 1, 0);
    if (torso) {
      const m = new THREE.Matrix4().copy(rootInv)
        .multiply(torso.parent.matrixWorld).invert();
      this._up.set(m.elements[4], m.elements[5], m.elements[6]);
    }

    // ---- per-frame records ----------------------------------------------
    this._mul = [];
    const mulRec = (bone) => {
      const r = { bone, k: 1, base: bone.position.clone(), last: bone.position.clone() };
      this._mul.push(r);
      return r;
    };
    this._spineM = spine.map(mulRec);
    this._exitM = legRoots.map(mulRec);
    if (neck) this._exitM.push(mulRec(neck));
    if (tailRoot) this._exitM.push(mulRec(tailRoot));
    this._segM = legSegs.map(mulRec);
    this._tailM = tailLinks.map(mulRec);
    this._headM = head ? mulRec(head) : null;

    const rotRec = (bone, axisWorld, sign) => ({
      bone, sign,
      axis: axisWorld.clone().applyQuaternion(rest.get(bone).wq.clone().invert()).normalize(),
      off: new THREE.Quaternion(),
      base: bone.quaternion.clone(), last: bone.quaternion.clone(),
    });
    this._earR = ears.map(([b, sign]) => rotRec(b, FORWARD, sign));
    this._tailR = tails.map((b) => rotRec(b, LEFT, 1));
    this._rot = this._earR.concat(this._tailR);

    this._jaw = jaw && head ? {
      bone: jaw, k: 0,
      axis: FORWARD.clone().applyQuaternion(rest.get(head).wq.clone().invert()).normalize(),
      base: jaw.position.clone(), last: jaw.position.clone(),
    } : null;

    this._lift = torso ? {
      bone: torso, vec: new THREE.Vector3(),
      base: torso.position.clone(), last: torso.position.clone(),
    } : null;

    // ---- scale targets ---------------------------------------------------
    const sref = (bone) => (bone ? { bone, s0: cur.get(bone).s } : null);
    this._sTorso = sref(torso);
    this._sNeck = sref(neck);
    this._sHead = sref(head);
    this._sJaw = sref(jaw);
    this._sEars = ears.map(([b]) => sref(b));
    this._sLegs = legRoots.map(sref);
    this._sTailRoot = sref(tailRoot);
    // The tip's long axis is the dominant component of its own offset from
    // its parent: exact on the generated rig, within the links' small rest
    // bends on the toon, and a leaf bone cannot shear anything below it.
    this._sTip = null;
    if (tailTip) {
      const p = cur.get(tailTip).p;
      let axis = 0;
      if (Math.abs(p.y) > Math.abs(p.getComponent(axis))) axis = 1;
      if (Math.abs(p.z) > Math.abs(p.getComponent(axis))) axis = 2;
      this._sTip = { bone: tailTip, s0: cur.get(tailTip).s, axis };
    }
  }

  /**
   * Cheap by contract -- runs on every slider input event.  Writes the
   * scales (safe once: the mixer never touches bone.scale) and refreshes the
   * factors postMixer() re-applies.  Everything derives from the captured
   * basis, so applying look B after look A gives exactly what applying B
   * alone would.
   */
  apply(look) {
    const c = look.chonk, L = look.leg_length, T = look.leg_thickness;
    const H = look.head_size, F = look.tail_fluff, TL = look.tail_length;

    this.root.scale.copy(this._scale0).multiplyScalar(look.size);

    setS(this._sTorso, c);
    setS(this._sNeck, 1 / c);
    setS(this._sHead, H);
    setS(this._sJaw, look.muzzle);
    for (const r of this._sEars) setS(r, look.ear_size / H);
    for (const r of this._sLegs) setS(r, T / c);
    setS(this._sTailRoot, F / c);
    if (this._sTip) {
      const r = this._sTip;
      r.bone.scale.copy(r.s0);
      r.bone.scale.setComponent(r.axis, r.s0.getComponent(r.axis) * (TL / F));
    }

    const spineK = look.body_length / c, exitK = 1 / c;
    const segK = L / T, tailK = TL / F;
    for (const r of this._spineM) r.k = spineK;
    for (const r of this._exitM) r.k = exitK;
    for (const r of this._segM) r.k = segK;
    for (const r of this._tailM) r.k = tailK;
    if (this._headM) this._headM.k = look.neck_length;
    if (this._jaw) this._jaw.k = look.muzzle - 1;
    if (this._lift) this._lift.vec.copy(this._up).multiplyScalar((L - 1) * this._drop);

    const tilt = RAD(look.ear_tilt), curl = RAD(look.tail_curl);
    for (const r of this._earR) r.off.setFromAxisAngle(r.axis, r.sign * tilt);
    for (const r of this._tailR) r.off.setFromAxisAngle(r.axis, curl);
  }

  /**
   * Every frame, after mixer.update() and before the render.  Each record
   * re-bases off the bone's current value only when someone else wrote it
   * since our last write (the .equals checks are exact, against the exact
   * values written below), so animated bones follow their clips and
   * unanimated ones never compound.  No allocation on this path.
   */
  postMixer() {
    const mul = this._mul;
    for (let i = 0; i < mul.length; i++) {
      const r = mul[i], p = r.bone.position;
      if (!p.equals(r.last)) r.base.copy(p);
      p.copy(r.base).multiplyScalar(r.k);
      r.last.copy(p);
    }
    const l = this._lift;
    if (l) {
      const p = l.bone.position;
      if (!p.equals(l.last)) l.base.copy(p);
      p.copy(l.base).add(l.vec);
      l.last.copy(p);
    }
    const j = this._jaw;
    if (j) {
      const p = j.bone.position;
      if (!p.equals(j.last)) j.base.copy(p);
      p.copy(j.base).addScaledVector(j.axis, j.k * j.base.dot(j.axis));
      j.last.copy(p);
    }
    const rot = this._rot;
    for (let i = 0; i < rot.length; i++) {
      const r = rot[i], q = r.bone.quaternion;
      if (!q.equals(r.last)) r.base.copy(q);
      q.copy(r.base).multiply(r.off);
      r.last.copy(q);
    }
  }

  /**
   * Restore every captured channel exactly.  Channels the clips animate get
   * re-posed on the next mixer update regardless; every other channel --
   * all the scales, and any bone the clips skip -- was pristine when the
   * constructor captured it, so the restore is the rest pose in effect.
   */
  dispose() {
    for (const r of this._rest) {
      r.bone.position.copy(r.p);
      r.bone.quaternion.copy(r.q);
      r.bone.scale.copy(r.s);
    }
    this.root.scale.copy(this._scale0);
  }
}

function setS(r, k) {
  if (r) r.bone.scale.copy(r.s0).multiplyScalar(k);
}
